"use client";

import { useId, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { COUNTRY_CODES, countryName, type CountryCode } from "../lib/countries";
import { CountryFlag } from "./CountryFlag";

type Labels = {
  /** Shown in the empty box. */
  placeholder: string;
  /** Shown when nothing matches what was typed. */
  noMatch: string;
};

/**
 * `EX-IDN03` (ADR-0088 decision 7): pick a country by typing part of its name,
 * in the reader's language or in English, or its two-letter code. A combobox
 * over the 250 codes: arrows move, Enter picks, Escape puts the box back.
 * Bangladesh sits first until something is typed, because most people using
 * it live there. Used in Profile and settings, the Welcome screen and the
 * contest registration box.
 *
 * `codes` narrows the list to the countries a filter can actually find (the
 * Judge leaderboards' `facets.countries`, `EX-LDB03`); without it every code
 * is offered.
 */
export function CountryPicker({
  id,
  value,
  onChange,
  locale,
  labels,
  describedBy,
  codes,
}: {
  id: string;
  value: CountryCode | null;
  onChange: (code: CountryCode) => void;
  locale: string;
  labels: Labels;
  describedBy?: string;
  codes?: readonly CountryCode[];
}) {
  const listId = useId();
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const all = useMemo(() => {
    const collator = new Intl.Collator(locale === "bn" ? "bn" : "en");
    const source: readonly CountryCode[] = codes ?? COUNTRY_CODES;
    const rows = source.map((code) => ({ code, name: countryName(code, locale), english: countryName(code, "en") }));
    rows.sort((a, b) => (a.code === "BD" ? -1 : b.code === "BD" ? 1 : collator.compare(a.name, b.name)));
    return rows;
  }, [locale, codes]);

  const matches = useMemo(() => {
    const q = (query ?? "").trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (row) => row.name.toLowerCase().includes(q) || row.english.toLowerCase().includes(q) || row.code.toLowerCase() === q,
    );
  }, [all, query]);

  const shown = query ?? (value ? countryName(value, locale) : "");

  function pick(code: CountryCode) {
    onChange(code);
    setQuery(null);
    setOpen(false);
  }

  function move(step: number) {
    if (!open) setOpen(true);
    const next = Math.max(0, Math.min(matches.length - 1, active + step));
    setActive(next);
    listRef.current?.querySelector<HTMLElement>(`[data-index="${next}"]`)?.scrollIntoView?.({ block: "nearest" });
  }

  return (
    <div className="relative">
      <div className="relative">
        {value && query === null ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
            <CountryFlag code={value} locale={locale} decorative />
          </span>
        ) : null}
        <Input
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-describedby={describedBy}
          aria-activedescendant={open && matches[active] ? `${listId}-${matches[active].code}` : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder={labels.placeholder}
          value={shown}
          className={cn(value && query === null ? "pl-10" : undefined)}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setOpen(false);
            setQuery(null);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              move(1);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              move(-1);
            } else if (e.key === "Enter") {
              const row = matches[active];
              if (open && row) {
                e.preventDefault();
                pick(row.code);
              }
            } else if (e.key === "Escape") {
              setQuery(null);
              setOpen(false);
            }
          }}
        />
      </div>
      {open ? (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-[var(--radius)] border border-neutral-200 bg-white py-1 shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
        >
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-sm text-neutral-500">{labels.noMatch}</li>
          ) : (
            matches.map((row, index) => (
              <li
                key={row.code}
                id={`${listId}-${row.code}`}
                data-index={index}
                role="option"
                aria-selected={row.code === value}
                // Keep the focus in the box, so the pick lands before the blur closes the list.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(row.code)}
                onMouseEnter={() => setActive(index)}
                className={cn(
                  "flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-neutral-800",
                  index === active && "bg-neutral-100",
                  row.code === value && "font-semibold",
                )}
              >
                <CountryFlag code={row.code} locale={locale} decorative />
                <span className="truncate">{row.name}</span>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
